// src/features/deadlines/components/LohnscheinVersandModal.tsx
//
// Lohnschein-Versand an Arbeitnehmer (Stufe 3.2 / Punkt 4) — Frontend-Modal.
//
// Öffnet sich beim Erledigen der Frist "Lohnschein-Versand" und ruft die
// Edge Function `lohnschein-versand` zuerst im Vorschau-Modus auf:
//   - Kontakt-Sperre (kein Kontakt mit Portal-Zugang) -> Beauftragung blockiert
//   - Direktversand-Liste (AN mit E-Mail + IBAN)
//   - Nachlade-Liste (AN ohne vollständige Versanddaten im Spiegel)
//   - Dedup-Übersprungene (bereits bedient)
//   - Hinweis, wenn noch keine Dokumente vorliegen (Auftrag wartet)
//   - Hinweis, wenn das Ruhefenster noch offen ist (Versand nach Ablauf)
//
// Ablauf (Weg A, asynchron, Punkt 4): Der Klick auf "Versand beauftragen" legt
// SOFORT einen Auftrag im Status 'wartet' an — auch ohne Dokumente, auch bei
// offenem Ruhefenster. Ein Worker prüft dann laufend, ob die Dokumente da sind
// und das Ruhefenster geschlossen ist, lädt fehlende Credentials nach und löst
// den Versand automatisch aus.
//
// PERSONENKREIS UND ZUSTELLUNGSNACHWEIS (28.08.2026): Ein Lauf kann auf interne
// oder externe Mitarbeiter begrenzt sein. Wer deshalb nicht dazugehoert, wird
// eigens ausgewiesen, damit vor dem Versand sichtbar ist, wer bewusst fehlt.
// Ebenso wird bei bereits belieferten Mitarbeitern unterschieden, ob die
// Auslieferung aus diesem oder aus einem anderen Lauf stammt: Letzteres deutet
// auf eine nachtraeglich geaenderte Zuordnung hin und gehoert vor Augen.
//
// TERMINIERTER VERSAND (28.08.2026): Über den Umschalter "Sofort senden" /
// "Geplant senden" kann ein Versandzeitpunkt gewählt werden (Berliner Zeit,
// umgerechnet über die zentrale Utility berlinTime). Der Zeitpunkt steuert
// ausschliesslich den Versand. Das Nachladen fehlender Arbeitnehmerdaten aus
// DATEV startet unabhängig davon, sobald die Lohnscheine im Spiegel liegen, so
// dass zum Termin nichts mehr offen ist. Ein gesetzter Zeitpunkt überstimmt das
// Ruhefenster; ohne Zeitpunkt bleibt das Verhalten unverändert.
//
// VERSANDSPERRE BEI FEHLENDER ZUORDNUNG (01.10.2026): Bei Kunden mit Zuordnung
// intern/extern sperrt die Vorschau die Beauftragung, solange Arbeitnehmer mit
// Lohnunterlagen der Periode nicht bestätigt zugeordnet sind oder nicht im
// Mitarbeiter-Spiegel stehen (block_reason 'nicht_zugeordnet'). Die Sperre wird
// im roten Sperrbereich zusammen mit der Liste der betroffenen Arbeitnehmer
// angezeigt.
//
// Frist-Erledigung (Weg A): Nach der Auftrags-Anlage wird die Frist NICHT als
// erledigt markiert — das macht der Worker erst nach dem tatsächlichen Versand.
// Die Kachel zeigt bis dahin den gelben "beauftragt/wartend"-Zustand. Deshalb
// ruft das Modal onSuccess(false) (Frist NICHT setzen). Der Sekundär-Button
// "Ohne Versand als erledigt markieren" ruft onSuccess(true) und erledigt die
// Frist sofort über den bestehenden Statuspfad.
//
// Der Backdrop nutzt exakt rgba(0, 0, 0, 0.5), damit der Auto-Refresh-Guard
// in useDeadlines das offene Modal erkennt.

import React, { useCallback, useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { Customer } from '../../../shared/types';
import { supabase } from '../../../shared/services/supabase';
import {
  berlinLocalToUtcIso,
  berlinNowLocalString,
  formatBerlinDateTime
} from '../../../shared/utils/berlinTime';
import { edgeFunctionFehlertext } from '../../../shared/utils/edgeFunctionFehler';

interface PreviewDirectRow {
  personalnummer: number;
  name: string;
  email: string | null;
  typen: string[];
}

interface PreviewSammelRow {
  personalnummer: number;
  name: string;
  grund: string;
  typen: string[];
}

interface PreviewSkippedRow {
  personalnummer: number;
  name: string;
  // 'bereits_versendet' = Auslieferung derselben Periode (bisheriges Verhalten),
  // 'anderer_lauf' = Auslieferung stammt aus einem anderen Lohnlauf.
  grund?: 'bereits_versendet' | 'anderer_lauf';
  frueherer_lohnlauf?: number | null;
}

// Wegen des Personenkreises nicht Teil dieses Laufs.
interface PreviewAusgefiltertRow {
  personalnummer: number;
  name: string;
  sichtbarkeit: 'extern' | 'intern' | 'offen' | 'verfallen';
}

// Versandsperre: noch nicht bestätigt zugeordnet oder nicht im Spiegel.
interface PreviewNichtZugeordnetRow {
  personalnummer: number;
  name: string;
  grund: 'nicht_im_spiegel' | 'offen' | 'verfallen';
  grund_text: string;
}

interface PreviewKontaktRow {
  id: string;
  name: string;
  email: string | null;
  hat_token: boolean;
}

interface PreviewResponse {
  blocked: boolean;
  block_reason?: string;
  message?: string;
  freigabe_um?: string;
  // Punkt 4: noch keine Dokumente im Spiegel — Auftrag ist trotzdem beauftragbar
  keine_dokumente?: boolean;
  // Punkt 4: Ruhefenster noch offen — nur Hinweis, keine Blockade
  ruhefenster_offen?: boolean;
  // Terminierter Versand: bei bereits laufendem Auftrag der dort hinterlegte
  // Versandzeitpunkt (UTC), damit er hier angezeigt werden kann.
  geplant_ab?: string | null;
  periode: { year: number; month: number; label: string };
  direct?: PreviewDirectRow[];
  sammelpaket?: PreviewSammelRow[];
  skipped_dedup?: PreviewSkippedRow[];
  sammelpaket_kontakte?: PreviewKontaktRow[];
  kontakt_probleme?: string[];
  // Personenkreis dieses Laufs und die dadurch ausgeschlossenen Mitarbeiter.
  personenkreis?: 'alle' | 'intern' | 'extern';
  ausgefiltert?: PreviewAusgefiltertRow[];
  // Versandsperre bei fehlender Zuordnung (block_reason 'nicht_zugeordnet').
  nicht_zugeordnet?: PreviewNichtZugeordnetRow[];
}

// Antwort der Auftrags-Anlage (mode='execute', Weg A). Der Klick legt einen
// Auftrag im Status 'wartet' an; der Versand läuft asynchron über den Worker.
interface ExecuteResponse {
  auftrag_angelegt?: boolean;
  bereits_in_arbeit?: boolean;
  auftrag_id?: string;
  periode: { year: number; month: number; label: string };
  status?: string;
  message?: string;
  geplant_ab?: string | null;
}

interface LohnscheinVersandModalProps {
  isOpen: boolean;
  customer: Customer;
  year: number;   // Arbeitsmonat des Kunden
  month: number;
  // Zwei-Lauf-Modell: welcher Lohnlauf und die bereits pro Lauf berechnete
  // Versandperiode (Inhalts-/Abrechnungsmonat), die ans Backend weitergereicht
  // werden.
  lohnlauf: 1 | 2;
  versandJahr: number;
  versandMonat: number;
  // Punkt 4: setzeBoolean=false nach Auftrags-Anlage (Worker erledigt die Frist
  // nach Versand), setzeBoolean=true beim "Ohne Versand als erledigt markieren".
  onSuccess: (setzeBoolean: boolean) => Promise<void> | void;
  onClose: () => void;
}

type Phase = 'loading' | 'blocked' | 'preview' | 'executing' | 'result' | 'error';

// Fehler-Body eines FunctionsHttpError auslesen: gemeinsamer Helfer
// edgeFunctionFehlertext (src/shared/utils/edgeFunctionFehler.ts).

export const LohnscheinVersandModal: React.FC<LohnscheinVersandModalProps> = ({
  isOpen,
  customer,
  year,
  month,
  lohnlauf,
  versandJahr,
  versandMonat,
  onSuccess,
  onClose
}) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [result, setResult] = useState<ExecuteResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fristFehler, setFristFehler] = useState<string | null>(null);
  const [markingDone, setMarkingDone] = useState(false);
  // Terminierter Versand: Sofort/Geplant plus Berliner Wanduhrzeit fürs
  // datetime-local-Feld. Gleiches Muster wie UploadModal und SecureDocumentModal.
  const [scheduleMode, setScheduleMode] = useState<'now' | 'later'>('now');
  const [scheduledLocal, setScheduledLocal] = useState('');

  const loadPreview = useCallback(async () => {
    setPhase('loading');
    setErrorMsg(null);
    setPreview(null);
    setResult(null);
    setFristFehler(null);

    const identityToken = localStorage.getItem('authToken');
    if (!identityToken) {
      setErrorMsg('Keine Anmelde-Identität gefunden — bitte im Intranet neu einloggen.');
      setPhase('error');
      return;
    }

    const { data, error } = await supabase.functions.invoke('lohnschein-versand', {
      body: {
        mode: 'preview',
        identity_token: identityToken,
        customer_id: customer.id,
        year,
        month,
        lohnlauf,
        versand_jahr: versandJahr,
        versand_monat: versandMonat
      }
    });

    if (error) {
      setErrorMsg(await edgeFunctionFehlertext(error, 'Vorschau konnte nicht geladen werden.'));
      setPhase('error');
      return;
    }

    const previewData = data as PreviewResponse;
    setPreview(previewData);
    setPhase(previewData.blocked ? 'blocked' : 'preview');
  }, [customer.id, year, month, lohnlauf, versandJahr, versandMonat]);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
    }
  }, [isOpen, loadPreview]);

  const handleExecute = async () => {
    setErrorMsg(null);

    // Zeitpunkt-Pflicht bei "Geplant senden". Die Umrechnung der Berliner
    // Wanduhrzeit nach UTC übernimmt die zentrale Utility; die verbindliche
    // Vergangenheitsprüfung passiert zusätzlich serverseitig.
    let geplantAb: string | null = null;
    if (scheduleMode === 'later') {
      if (!scheduledLocal) {
        setErrorMsg('Bitte wählen Sie einen Versandzeitpunkt.');
        return;
      }
      try {
        geplantAb = berlinLocalToUtcIso(scheduledLocal);
      } catch (_e) {
        setErrorMsg('Der gewählte Versandzeitpunkt ist ungültig.');
        return;
      }
    }

    setPhase('executing');

    const identityToken = localStorage.getItem('authToken');
    if (!identityToken) {
      setErrorMsg('Keine Anmelde-Identität gefunden — bitte im Intranet neu einloggen.');
      setPhase('preview');
      return;
    }

    const { data, error } = await supabase.functions.invoke('lohnschein-versand', {
      body: {
        mode: 'execute',
        identity_token: identityToken,
        customer_id: customer.id,
        year,
        month,
        lohnlauf,
        versand_jahr: versandJahr,
        versand_monat: versandMonat,
        geplant_ab: geplantAb
      }
    });

    if (error) {
      setErrorMsg(await edgeFunctionFehlertext(error, 'Auftrag konnte nicht angelegt werden.'));
      setPhase('preview');
      return;
    }

    const executeData = data as ExecuteResponse;
    setResult(executeData);

    // Auftrag angelegt (oder lief bereits). Weg A: Die Frist NICHT als erledigt
    // markieren — der Worker erledigt sie nach dem tatsächlichen Versand. Nur
    // neu laden, damit die Kachel den gelben "beauftragt/wartend"-Zustand zeigt.
    if (executeData.auftrag_angelegt || executeData.bereits_in_arbeit) {
      try {
        await onSuccess(false);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setFristFehler(
          `Der Auftrag wurde angelegt, aber die Ansicht konnte nicht aktualisiert werden: ${msg}. ` +
          `Der Versand läuft trotzdem automatisch weiter.`
        );
      }
    }

    setPhase('result');
  };

  // Erhält das bisherige freie Abhaken: erledigt die Frist SOFORT ohne Versand
  // (z.B. Periode bereits manuell bedient oder bewusst kein Versand gewünscht).
  const handleMarkDoneWithoutSend = async () => {
    setMarkingDone(true);
    try {
      await onSuccess(true);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(`Frist konnte nicht als erledigt markiert werden: ${msg}`);
    } finally {
      setMarkingDone(false);
    }
  };

  if (!isOpen) return null;

  const direct = preview?.direct ?? [];
  const sammel = preview?.sammelpaket ?? [];
  const skipped = preview?.skipped_dedup ?? [];
  const ausgefiltert = preview?.ausgefiltert ?? [];
  const personenkreis = preview?.personenkreis ?? 'alle';
  const nichtZugeordnet = preview?.nicht_zugeordnet ?? [];
  // Bereits in einem ANDEREN Lauf belieferte Mitarbeiter. Diese Faelle sind der
  // Hinweis darauf, dass sich die Zuordnung zwischen intern und extern nach dem
  // ersten Versand geaendert hat.
  const skippedAndererLauf = skipped.filter(s => s.grund === 'anderer_lauf');
  const kontakte = preview?.sammelpaket_kontakte ?? [];
  const keineDokumente = !!preview?.keine_dokumente;
  const ruhefensterOffen = !!preview?.ruhefenster_offen;
  const terminGewaehlt = scheduleMode === 'later';
  // Nichts zu versenden gilt nur, wenn Dokumente da sind, aber alle bereits
  // bedient wurden. Ohne Dokumente ist der Auftrag trotzdem beauftragbar.
  const nichtsZuVersenden = !keineDokumente && direct.length === 0 && sammel.length === 0;
  const versandMoeglich = phase === 'preview'
    && !nichtsZuVersenden
    && !(terminGewaehlt && !scheduledLocal);

  const freigabeText = preview?.freigabe_um
    ? new Date(preview.freigabe_um).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    : null;

  const sectionTitle = (text: string) => (
    <div style={{ fontWeight: 600, fontSize: '13px', color: '#374151', margin: '16px 0 6px' }}>
      {text}
    </div>
  );

  const listBox: React.CSSProperties = {
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    maxHeight: '160px',
    overflowY: 'auto',
    fontSize: '12px'
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '5px 10px',
    borderBottom: '1px solid #F3F4F6'
  };

  const primaryButton: React.CSSProperties = {
    padding: '10px 18px',
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer'
  };

  const secondaryButton: React.CSSProperties = {
    padding: '10px 18px',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const subtleButton: React.CSSProperties = {
    padding: '8px 12px',
    backgroundColor: 'transparent',
    color: '#6B7280',
    border: '1px dashed #D1D5DB',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer'
  };

  // Beschriftung des Hauptbuttons. Bei gewähltem Termin wird geplant statt
  // beauftragt; die Mitarbeiterzahl bleibt erhalten, wo sie bekannt ist.
  const primaryButtonLabel = (): string => {
    if (phase === 'executing') {
      return terminGewaehlt ? 'Versand wird geplant …' : 'Auftrag wird angelegt …';
    }
    if (keineDokumente) {
      return terminGewaehlt
        ? 'Versand planen (Dokumente ausstehend)'
        : 'Versand beauftragen (Dokumente ausstehend)';
    }
    const anzahl = direct.length + sammel.length;
    return terminGewaehlt
      ? `Versand planen (${anzahl} Arbeitnehmer)`
      : `Versand beauftragen (${anzahl} Arbeitnehmer)`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '640px',
          maxWidth: '92vw',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
        }}
      >
        <div style={{ marginBottom: '4px', fontSize: '18px', fontWeight: 700, color: '#111827' }}>
          Lohnschein-Versand — {customer.company}
        </div>
        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
          {preview?.periode
            ? `Versand der Lohnscheine für ${preview.periode.label} an die Arbeitnehmer`
            : 'Vorschau wird geladen …'}
        </div>

        {errorMsg && (
          <div style={{
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            border: '1px solid #FCA5A5',
            borderRadius: '6px',
            padding: '10px 12px',
            fontSize: '13px',
            marginBottom: '12px'
          }}>
            {errorMsg}
          </div>
        )}

        {phase === 'loading' && (
          <div style={{ padding: '30px 0', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
            Vorschau wird berechnet …
          </div>
        )}

        {/* Blockiert = Kontakt-Sperre (rot) ODER bereits laufender Auftrag (gelb/Info) */}
        {phase === 'blocked' && preview && (
          preview.block_reason === 'auftrag_laeuft' ? (
            <div style={{
              backgroundColor: '#FEF3C7',
              color: '#92400E',
              border: '1px solid #FCD34D',
              borderRadius: '6px',
              padding: '12px 14px',
              fontSize: '13px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>Versand-Auftrag läuft bereits</div>
              <div>{preview.message}</div>
              {preview.geplant_ab && (
                <div style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600
                }}>
                  <Clock size={14} />
                  Geplanter Versand: {formatBerlinDateTime(preview.geplant_ab)} Uhr
                </div>
              )}
            </div>
          ) : (
            <div style={{
              backgroundColor: '#FEE2E2',
              color: '#991B1B',
              border: '1px solid #FCA5A5',
              borderRadius: '6px',
              padding: '12px 14px',
              fontSize: '13px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>Versand kann nicht beauftragt werden</div>
              <div>{preview.message}</div>
              {/* Versandsperre bei fehlender Zuordnung: betroffene Arbeitnehmer auflisten */}
              {preview.block_reason === 'nicht_zugeordnet' && nichtZugeordnet.length > 0 && (
                <div style={{ ...listBox, marginTop: '10px', backgroundColor: '#FFFFFF', color: '#374151' }}>
                  {nichtZugeordnet.map(row => (
                    <div key={row.personalnummer} style={rowStyle}>
                      <span>PNr {row.personalnummer} — {row.name}</span>
                      <span style={{ color: '#B45309' }}>{row.grund_text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        )}

        {(phase === 'preview' || phase === 'executing') && preview && (
          <>
            {/* Punkt 4: Hinweis, wenn noch keine Dokumente vorliegen */}
            {keineDokumente && (
              <div style={{
                backgroundColor: '#EFF6FF',
                color: '#1E40AF',
                border: '1px solid #BFDBFE',
                borderRadius: '6px',
                padding: '12px 14px',
                fontSize: '13px',
                marginBottom: '8px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Dokumente noch nicht im Spiegel</div>
                <div>
                  Für diese Periode liegen noch keine Lohnschein-Dokumente vor. Der Auftrag kann
                  trotzdem beauftragt werden — der Versand erfolgt automatisch, sobald die Dokumente
                  eintreffen und das Ruhefenster abgelaufen ist.
                </div>
              </div>
            )}

            {/* Ruhefenster-Hinweis. Bei gewähltem Termin überstimmt der Termin das
                Ruhefenster, deshalb dort eine andere Aussage. */}
            {!keineDokumente && ruhefensterOffen && (
              <div style={{
                backgroundColor: '#FEF3C7',
                color: '#92400E',
                border: '1px solid #FCD34D',
                borderRadius: '6px',
                padding: '10px 12px',
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                {terminGewaehlt ? (
                  <>
                    Die Dokumente sind noch im Zulauf (jüngste Änderung vor weniger als 5 Stunden).
                    Der von Ihnen gewählte Zeitpunkt hat Vorrang: es wird zum Termin versendet, auch
                    wenn das Ruhefenster dann noch nicht abgelaufen wäre. Bitte stellen Sie sicher,
                    dass die Abrechnung bis dahin abgeschlossen ist.
                  </>
                ) : (
                  <>
                    Die Dokumente sind noch im Zulauf (jüngste Änderung vor weniger als 5 Stunden).
                    Der Auftrag kann beauftragt werden; der Versand erfolgt automatisch nach Ablauf des
                    Ruhefensters{freigabeText ? ` (voraussichtlich ${freigabeText} Uhr)` : ''}.
                  </>
                )}
              </div>
            )}

            {!keineDokumente && (
              <>
                {sectionTitle(`Direktversand per E-Mail (${direct.length})`)}
                {direct.length > 0 ? (
                  <div style={listBox}>
                    {direct.map(row => (
                      <div key={row.personalnummer} style={rowStyle}>
                        <span>PNr {row.personalnummer} — {row.name}</span>
                        <span style={{ color: '#6B7280' }}>{row.email} · {row.typen.join('+')}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>Keine Arbeitnehmer für den Direktversand.</div>
                )}

                {sectionTitle(`Werden aus DATEV nachgeladen (${sammel.length})`)}
                {sammel.length > 0 ? (
                  <>
                    <div style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1E40AF',
                      border: '1px solid #BFDBFE',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      fontSize: '12px',
                      marginBottom: '8px'
                    }}>
                      Diese Arbeitnehmer haben aktuell keine vollständigen Versanddaten im Spiegel.
                      Beim Versand werden sie zuerst einzeln aus DATEV nachgeladen. Wer danach E-Mail
                      und IBAN hat, erhält den Lohnschein direkt per E-Mail; für alle übrigen geht
                      ein Sammelpaket an den Kunden.
                    </div>
                    <div style={listBox}>
                      {sammel.map(row => (
                        <div key={row.personalnummer} style={rowStyle}>
                          <span>PNr {row.personalnummer} — {row.name}</span>
                          <span style={{ color: '#B45309' }}>aktuell: {row.grund} · {row.typen.join('+')}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>
                      Empfänger eines etwaigen Sammelpakets:{' '}
                      {kontakte.length > 0
                        ? kontakte.map(k => `${k.name} (${k.email ?? 'ohne E-Mail'})`).join(', ')
                        : 'kein Kontakt mit Flag "Erhält Lohnschein-Bericht"'}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    Alle Arbeitnehmer können direkt beliefert werden — kein Nachladen nötig.
                  </div>
                )}

                {ausgefiltert.length > 0 && (
                  <>
                    {sectionTitle(
                      personenkreis === 'intern'
                        ? `Nicht in diesem Lauf, weil extern (${ausgefiltert.length})`
                        : `Nicht in diesem Lauf, weil nicht extern (${ausgefiltert.length})`
                    )}
                    <div style={{
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      fontSize: '12px',
                      marginBottom: '8px'
                    }}>
                      {personenkreis === 'intern'
                        ? 'Dieser Lauf ist auf interne Mitarbeiter begrenzt. Die folgenden Arbeitnehmer sind als extern ' +
                          'bestätigt und bleiben deshalb außen vor. Ihre Lohnscheine gehen nicht verloren, sie stehen ' +
                          'weiterhin für den externen Lauf bereit.'
                        : 'Dieser Lauf ist auf externe Mitarbeiter begrenzt. Die folgenden Arbeitnehmer sind nicht als ' +
                          'extern bestätigt und bleiben deshalb außen vor. Ihre Lohnscheine stehen weiterhin für den ' +
                          'internen Lauf bereit.'}
                    </div>
                    <div style={listBox}>
                      {ausgefiltert.map(row => (
                        <div key={row.personalnummer} style={rowStyle}>
                          <span>PNr {row.personalnummer} — {row.name}</span>
                          <span style={{ color: '#6B7280' }}>
                            {row.sichtbarkeit === 'extern'
                              ? 'als extern bestätigt'
                              : row.sichtbarkeit === 'verfallen'
                                ? 'als extern bestätigt, Stammdaten geändert'
                                : row.sichtbarkeit === 'intern'
                                  ? 'als intern bestätigt'
                                  : 'nicht als extern bestätigt'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {skippedAndererLauf.length > 0 && (
                  <div style={{
                    backgroundColor: '#FEF3C7',
                    color: '#92400E',
                    border: '1px solid #FCD34D',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    fontSize: '12px',
                    marginTop: '14px'
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {skippedAndererLauf.length === 1
                        ? 'Ein Mitarbeiter wurde bereits über einen anderen Lohnlauf beliefert'
                        : `${skippedAndererLauf.length} Mitarbeiter wurden bereits über einen anderen Lohnlauf beliefert`}
                    </div>
                    <div>
                      Sie werden übersprungen, damit niemand denselben Lohnschein zweimal erhält. Das kann
                      darauf hindeuten, dass sich die Zuordnung zwischen intern und extern nach dem ersten
                      Versand geändert hat. Bitte prüfen, ob das so gewollt ist.
                    </div>
                  </div>
                )}

                {skipped.length > 0 && (
                  <>
                    {sectionTitle(`Bereits bedient — wird übersprungen (${skipped.length})`)}
                    <div style={listBox}>
                      {skipped.map(row => (
                        <div key={row.personalnummer} style={rowStyle}>
                          <span>PNr {row.personalnummer} — {row.name}</span>
                          <span style={{ color: row.grund === 'anderer_lauf' ? '#B45309' : '#6B7280' }}>
                            {row.grund === 'anderer_lauf'
                              ? `bereits über Lohnlauf ${row.frueherer_lohnlauf ?? '?'} beliefert`
                              : 'Dokument für diese Periode existiert bereits'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {nichtsZuVersenden && (
                  <div style={{
                    backgroundColor: '#D1FAE5',
                    color: '#065F46',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    fontSize: '13px',
                    marginTop: '14px'
                  }}>
                    {ausgefiltert.length > 0 && skipped.length === 0
                      ? `Für diesen Lauf bleibt niemand übrig: Alle ${ausgefiltert.length} Arbeitnehmer mit Dokumenten ` +
                        `für diese Periode gehören nicht zum Personenkreis dieses Laufs. Sie werden über den anderen ` +
                        `Lauf beliefert. Die Frist kann ohne Versand als erledigt markiert werden.`
                      : ausgefiltert.length > 0
                        ? `Für diesen Lauf bleibt niemand übrig: ${skipped.length} Arbeitnehmer wurden bereits bedient, ` +
                          `${ausgefiltert.length} gehören nicht zum Personenkreis dieses Laufs. ` +
                          `Die Frist kann ohne erneuten Versand als erledigt markiert werden.`
                        : `Alle Arbeitnehmer mit Dokumenten für diese Periode wurden bereits bedient. ` +
                          `Die Frist kann ohne erneuten Versand als erledigt markiert werden.`}
                  </div>
                )}
              </>
            )}

            {/* Versandzeitpunkt (Sofort / Geplant). Steuert ausschliesslich den
                Versand; das Nachladen fehlender Arbeitnehmerdaten startet
                unabhängig davon, sobald die Dokumente im Spiegel liegen. */}
            {!nichtsZuVersenden && (
              <div style={{ marginTop: '18px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Versandzeitpunkt
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{
                    display: 'inline-flex',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '6px',
                    padding: '3px'
                  }}>
                    <button
                      type="button"
                      onClick={() => setScheduleMode('now')}
                      disabled={phase === 'executing'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '5px 10px',
                        backgroundColor: scheduleMode === 'now' ? '#FFFFFF' : 'transparent',
                        color: scheduleMode === 'now' ? '#2563EB' : '#6B7280',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: scheduleMode === 'now' ? 600 : 500,
                        cursor: phase === 'executing' ? 'not-allowed' : 'pointer',
                        boxShadow: scheduleMode === 'now' ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
                      }}
                    >
                      Sofort senden
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduleMode('later')}
                      disabled={phase === 'executing'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '5px 10px',
                        backgroundColor: scheduleMode === 'later' ? '#FFFFFF' : 'transparent',
                        color: scheduleMode === 'later' ? '#2563EB' : '#6B7280',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: scheduleMode === 'later' ? 600 : 500,
                        cursor: phase === 'executing' ? 'not-allowed' : 'pointer',
                        boxShadow: scheduleMode === 'later' ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
                      }}
                    >
                      <Clock size={13} />
                      Geplant senden
                    </button>
                  </div>
                  {scheduleMode === 'later' && (
                    <input
                      type="datetime-local"
                      value={scheduledLocal}
                      min={berlinNowLocalString()}
                      onChange={(e) => setScheduledLocal(e.target.value)}
                      disabled={phase === 'executing'}
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#374151',
                        backgroundColor: '#FFFFFF'
                      }}
                    />
                  )}
                </div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '6px 0 0 0', lineHeight: '1.4' }}>
                  {scheduleMode === 'later'
                    ? 'Versendet wird zum gewählten Zeitpunkt (Berliner Zeit). Fehlende Arbeitnehmerdaten ' +
                      'werden bereits vorher aus DATEV nachgeladen, sobald die Lohnscheine vorliegen. Liegen ' +
                      'zum Termin keine Dokumente vor, wird nicht versendet und Sie erhalten eine Meldung.'
                    : 'Versendet wird automatisch, sobald die Lohnscheine vorliegen, alle Arbeitnehmerdaten ' +
                      'geklärt sind und das Ruhefenster von 5 Stunden abgelaufen ist.'}
                </p>
              </div>
            )}
          </>
        )}

        {phase === 'result' && result && (
          <>
            <div style={{
              backgroundColor: '#D1FAE5',
              color: '#065F46',
              borderRadius: '6px',
              padding: '12px 14px',
              fontSize: '13px',
              marginBottom: '12px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                {result.bereits_in_arbeit
                  ? 'Für diese Periode läuft bereits ein Versand-Auftrag.'
                  : result.geplant_ab
                    ? 'Auftrag angelegt — der Versand ist geplant.'
                    : 'Auftrag angelegt — der Versand läuft automatisch.'}
              </div>
              {result.message && <div style={{ marginTop: '4px' }}>{result.message}</div>}
              {result.geplant_ab && (
                <div style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600
                }}>
                  <Clock size={14} />
                  Geplanter Versand: {formatBerlinDateTime(result.geplant_ab)} Uhr
                </div>
              )}
              <div style={{ marginTop: '6px', fontSize: '12px' }}>
                Die Frist bleibt so lange als offen markiert (gelb), bis der Versand tatsächlich
                erfolgt ist — dann wird sie automatisch abgehakt.
              </div>
            </div>

            {fristFehler && (
              <div style={{
                backgroundColor: '#FEF3C7',
                color: '#92400E',
                borderRadius: '6px',
                padding: '10px 12px',
                fontSize: '12px',
                marginTop: '12px'
              }}>
                {fristFehler}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginTop: '20px'
        }}>
          <div>
            {(phase === 'preview' || phase === 'blocked' || phase === 'error') && preview?.block_reason !== 'auftrag_laeuft' && (
              <button
                style={subtleButton}
                onClick={handleMarkDoneWithoutSend}
                disabled={markingDone}
                title="Erledigt die Frist sofort, ohne einen Versand auszulösen"
              >
                {markingDone ? 'Wird markiert …' : 'Ohne Versand als erledigt markieren'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={secondaryButton}
              onClick={onClose}
              disabled={phase === 'executing'}
            >
              {phase === 'result' ? 'Schließen' : 'Abbrechen'}
            </button>
            {(phase === 'preview' || phase === 'executing') && !nichtsZuVersenden && (
              <button
                style={{
                  ...primaryButton,
                  opacity: versandMoeglich ? 1 : 0.5,
                  cursor: versandMoeglich ? 'pointer' : 'not-allowed'
                }}
                onClick={handleExecute}
                disabled={!versandMoeglich}
              >
                {primaryButtonLabel()}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
